import React, { useState, useEffect, useCallback } from 'react';
import { Search, ShoppingBag } from 'lucide-react';
import { AdminStoreInvoiceResource } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import StoreInvoiceDetailSlideOver from '../components/StoreInvoiceDetailSlideOver';

const statusBadge = (status: string) => {
  const styles: Record<string, string> = {
    paid: 'bg-green-100 text-green-800',
    unpaid: 'bg-yellow-100 text-yellow-800',
    expired: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${styles[status] ?? 'bg-gray-100 text-gray-800'}`}>
      {status.toUpperCase()}
    </span>
  );
};

const StoreInvoices: React.FC = () => {
  const [invoices, setInvoices] = useState<AdminStoreInvoiceResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'unpaid' | 'paid' | 'expired'>('');
  const [selectedInvoice, setSelectedInvoice] = useState<AdminStoreInvoiceResource | null>(null);
  const [slideOverOpen, setSlideOverOpen] = useState(false);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getAdminStoreInvoices(
        page,
        perPage,
        search || undefined,
        statusFilter ? { status: statusFilter } : undefined
      );
      setInvoices(response.data);
      setTotal(response.meta.total);
    } catch (error) {
      console.error('Error loading store invoices:', error);
      setInvoices([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, statusFilter]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput); setPage(1); }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const openDetail = (invoice: AdminStoreInvoiceResource) => {
    setSelectedInvoice(invoice);
    setSlideOverOpen(true);
  };

  const formatRupiah = (amount: number) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Store Invoices</h1>
        <p className="mt-1 text-sm text-gray-500">View and manage feature store purchase invoices.</p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by invoice number or company..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-9 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value as '' | 'unpaid' | 'paid' | 'expired'); setPage(1); }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="unpaid">Unpaid</option>
            <option value="paid">Paid</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white shadow rounded-lg p-8">
          <LoadingSpinner size="md" text="Loading invoices..." />
        </div>
      ) : invoices.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <ShoppingBag className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices found</h3>
          <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Invoice Number', 'Company', 'Feature', 'Grand Total', 'Status', 'Paid At', 'Created', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">{inv.invoiceNumber}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{inv.company.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{inv.storeItem.name}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{formatRupiah(inv.grandTotal)}</td>
                    <td className="px-4 py-3">{statusBadge(inv.status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {inv.paidAt ? new Date(inv.paidAt).toLocaleDateString('id-ID') : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(inv.createdAt).toLocaleDateString('id-ID')}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openDetail(inv)}
                        className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-gray-200">
            <Pagination
              currentPage={page}
              total={total}
              perPage={perPage}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}

      <StoreInvoiceDetailSlideOver
        invoice={selectedInvoice}
        isOpen={slideOverOpen}
        onClose={() => setSlideOverOpen(false)}
        onPaidSuccess={loadInvoices}
      />
    </div>
  );
};

export default StoreInvoices;
